import React, { useEffect, useMemo, useState } from 'react';

import { useToast } from '../../../components/ui/toast/ToastContext';
import { fetchWithCsrf } from '../../../utils/fetchWithCsrf';
import { camelizeKeys } from '../../../utils/camelizeKeys';

import Commercial from './areas/Commercial';
import Marketing from './areas/Marketing';
import Administrative from './areas/Administrative';
import Operations from './areas/Operations';
import Compliance from './areas/Compliance';
import Master from './areas/Master';

const areaTabs = [
  { key: 'master', label: 'Master', component: Master },
  { key: 'commercial', label: 'Comercial', component: Commercial },
  { key: 'operations', label: 'Operações', component: Operations },
  { key: 'marketing', label: 'Marketing', component: Marketing },
  { key: 'administrative', label: 'Administrativo', component: Administrative },
  { key: 'compliance', label: 'Compliance', component: Compliance },
];

const copy = (value) => JSON.parse(JSON.stringify(value));

const comparablePermissions = (profile) =>
  [...profile.permissions]
    .map(({ featureKey, level, scopeLevel }) => ({ featureKey, level, scopeLevel }))
    .sort((left, right) => left.featureKey.localeCompare(right.featureKey));

const profileChanged = (profile, originalProfiles) => {
  const original = originalProfiles.find((item) => item.id === profile.id);
  if (!original) return true;

  return JSON.stringify(comparablePermissions(profile)) !== JSON.stringify(comparablePermissions(original));
};

const masterFeatureVisible = (profile, featureKey) => {
  if (featureKey === 'system.access_management') return true;

  const directPermission = profile.permissions.find((permission) => permission.featureKey === featureKey);
  if (directPermission) return directPermission.level !== 'none';

  return true;
};

const Areas = () => {
  const [activeArea, setActiveArea] = useState(areaTabs[0].key);
  const [profiles, setProfiles] = useState([]);
  const [originalProfiles, setOriginalProfiles] = useState([]);
  const [features, setFeatures] = useState([]);
  const [dirtyProfileIds, setDirtyProfileIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { addToast } = useToast();

  useEffect(() => {
    let active = true;

    const load = async () => {
      setLoading(true);

      try {
        const response = await fetch('/app/master/access_profiles', {
          headers: { Accept: 'application/json' },
        });
        const payload = camelizeKeys(await response.json());

        if (!response.ok) throw new Error(payload.errors?.join(' ') || 'Não foi possível carregar os perfis.');
        if (!active) return;

        const loadedProfiles = payload.accessProfiles || [];
        setProfiles(loadedProfiles);
        setOriginalProfiles(copy(loadedProfiles));
        setFeatures(payload.features || []);
        setDirtyProfileIds(new Set());
      } catch (error) {
        if (active) addToast({ type: 'error', message: error.message });
      } finally {
        if (active) setLoading(false);
      }
    };

    load();

    return () => {
      active = false;
    };
  }, [addToast]);

  const currentArea = areaTabs.find((area) => area.key === activeArea) || areaTabs[0];
  const AreaContent = currentArea.component;

  const areaProfiles = useMemo(() => {
    if (activeArea === 'master') return profiles.filter((profile) => profile.adminMaster);

    return profiles.filter((profile) => profile.area === activeArea && !profile.adminMaster);
  }, [activeArea, profiles]);

  const handlePermissionChange = (profileId, featureKey, level) => {
    setProfiles((currentProfiles) => {
      const nextProfiles = currentProfiles.map((profile) => {
        if (profile.id !== profileId) return profile;

        const permissionExists = profile.permissions.some((permission) => permission.featureKey === featureKey);
        const permissions = permissionExists
          ? profile.permissions.map((permission) =>
              permission.featureKey === featureKey ? { ...permission, level } : permission
            )
          : [...profile.permissions, { featureKey, level, scopeLevel: 'no_scope' }];

        return { ...profile, permissions };
      });

      const changedProfile = nextProfiles.find((profile) => profile.id === profileId);
      setDirtyProfileIds((currentIds) => {
        const nextIds = new Set(currentIds);

        if (profileChanged(changedProfile, originalProfiles)) nextIds.add(profileId);
        else nextIds.delete(profileId);

        return nextIds;
      });

      return nextProfiles;
    });
  };

  const handleCancel = () => {
    setProfiles(copy(originalProfiles));
    setDirtyProfileIds(new Set());
  };

  const handleSave = async () => {
    const changedProfiles = profiles.filter((profile) => dirtyProfileIds.has(profile.id));
    if (changedProfiles.length === 0) return;

    setSaving(true);

    try {
      await Promise.all(
        changedProfiles.map(async (profile) => {
          const response = await fetchWithCsrf(`/app/master/access_profiles/${profile.id}`, {
            method: 'PATCH',
            headers: { Accept: 'application/json' },
            body: JSON.stringify({
              access_profile: {
                permissions: profile.permissions.map((permission) => ({
                  feature_key: permission.featureKey,
                  level: permission.level,
                  scope_level: permission.scopeLevel,
                })),
              },
            }),
          });

          const payload = await response.json();
          if (!response.ok) throw new Error(payload.errors?.join(' ') || `Erro ao salvar o perfil ${profile.name}.`);
        })
      );

      setOriginalProfiles(copy(profiles));
      setDirtyProfileIds(new Set());

      const changedMaster = changedProfiles.find((profile) => profile.adminMaster);
      if (changedMaster) {
        const items = features
          .filter((feature) => feature.sidebar && masterFeatureVisible(changedMaster, feature.featureKey))
          .map((feature) => ({
            icon: feature.icon,
            label: feature.label,
            href: feature.href,
            visible: true,
          }));

        window.dispatchEvent(new CustomEvent('admin-sidebar-items-updated', { detail: { items } }));
      }

      addToast({ type: 'success', message: 'Permissões atualizadas com sucesso.' });
    } catch (error) {
      addToast({ type: 'error', message: error.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="flex flex-col rounded-2xl bg-white px-6 py-6 shadow-sm">
      <div className="flex flex-col gap-5 xl:flex-row justify-between items-center">
        <div>
          <h2 className="text-lg font-semibold text-blueDarker">Acesso às áreas</h2>
          <p className="mt-1 text-sm text-blueSteel">
            {activeArea === 'master'
              ? 'Escolha quais módulos devem aparecer na sidebar do acesso Master.'
              : 'Defina o que cada cargo de cada área pode acessar por módulo.'}
          </p>
        </div>

        <div className="flex flex-wrap gap-3">
          {areaTabs.map((area) => (
            <button
              key={area.key}
              type="button"
              onClick={() => setActiveArea(area.key)}
              className={`min-w-custom150 rounded-full border px-5 py-2 text-sm font-medium transition-colors ${
                activeArea === area.key
                  ? 'border-tealMedium bg-tealMedium text-white'
                  : 'border-grayExtraLight2 bg-blueLight text-blueDark hover:border-tealSoft'
              }`}
            >
              {area.label}
            </button>
          ))}
        </div>
      </div>

      <div>
        {loading ? (
          <div className="flex h-64 items-center justify-center text-sm text-blueSteel">Carregando permissões...</div>
        ) : (
          <AreaContent profiles={areaProfiles} features={features} onPermissionChange={handlePermissionChange} />
        )}
      </div>

      <footer className="mt-6 flex flex-col-reverse justify-end gap-3 sm:flex-row">
        <button
          type="button"
          onClick={handleCancel}
          disabled={saving || dirtyProfileIds.size === 0}
          className="rounded-xl border-b-2 border-r-2 border-graySemiDark bg-blueUltraLight px-7 py-3 text-sm font-semibold text-grayDark transition-colors hover:bg-grayLight disabled:cursor-not-allowed disabled:opacity-50"
        >
          CANCELAR
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || dirtyProfileIds.size === 0}
          className="rounded-xl border-b-2 border-r-2 border-tealBase bg-tealMedium px-7 py-3 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {saving ? 'SALVANDO...' : 'SALVAR ALTERAÇÕES'}
        </button>
      </footer>
    </section>
  );
};

export default Areas;
